import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "@/taskpane/contexts/auth/AuthContext";

import FiliationList from "@/taskpane/modules/folder/filiation/FiliationList";
import { useGetUserAttributesQuery } from '@/taskpane/services/auth.hook';
import StyledSelect from "@/taskpane/components/styled-select/StyledSelect";
import { WarningTooltip } from "@/taskpane/utils/tooltips";
import { SnackbarCloseIcon } from "@/taskpane/components/snackbar-provider";
import {
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import useFiliationData from "@/taskpane/hooks/filiation";
import { useGetFiliationQuery, useSetFiliationMutation, useLockFiliationMutation } from "@/taskpane/services/filiation.hook";
import { useNavigate, useParams } from "react-router-dom";
import { HelpOutline, Close as CloseIcon } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueConfirmSnackbar } from "@/taskpane/components/snackbar-provider/SnackbarProvider";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import { useWindowSize } from "@uidotdev/usehooks";
import { formatParisTime } from "@/taskpane/utils/format-time";

interface FilteredData {
  filteredLots: any[];
  filteredParcelles: any[];
  filteredVolumes: any[];
}

/**
 * Main component to render the filiation process.
 * @returns {JSX.Element} The rendered component.
 */
export default function Filiation(): JSX.Element {
  const [blockUi, setBlockUi] = useState<boolean>(false);
  const [selectedCommune, setSelectedCommune] = useState<number>(-1);
  const [selectedProperties, setSelectedProperties] = useState<number[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isEmptyPropertiesModalOpen, setIsEmptyPropertiesModalOpen] = useState<boolean>(false);
  const { isAuthenticated, tokens } = useContext(AuthContext);
  const hasValidAccessToken = Boolean(tokens?.AccessToken && tokens.AccessToken.length > 0);
  const { data: userData } = useGetUserAttributesQuery({
    enabled: isAuthenticated && hasValidAccessToken,
  });
  const client_id = userData?.crpcen;
  const [previousModalResponse, setPreviousModalResponse] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>('');
  const [isPredictorModalOpen, setIsPredictorModalOpen] = useState<boolean>(false);
  const { height } = useWindowSize();
  const isDevEnvironment = useMemo(() => {
    const apiUrl = process.env.REACT_APP_BASE_URL_API_ADDIN || '';
    return apiUrl.includes('dev');
  }, []);

  const navigate = useNavigate();

  const { folderId, flowId } = useParams();

  const queryClient = useQueryClient();

  const { communes, parcellesWithNoChildren, lots, volumesWithNoChildren, getValidatedProperties, isLoading } =
    useFiliationData(folderId || "", flowId || "");

  const { data: filiationData } = useGetFiliationQuery(folderId || "", flowId || "");

  // Vérifier si aucun bien n'a été détecté
  const isPropertiesEmpty = useMemo(() => {
    return (
      !isLoading &&
      lots.length === 0 &&
      parcellesWithNoChildren.length === 0 &&
      volumesWithNoChildren.length === 0
    );
  }, [isLoading, lots, parcellesWithNoChildren, volumesWithNoChildren]);


  // Ouvrir le modal quand aucun bien n'est détecté
  useEffect(() => {
    if (isPropertiesEmpty) {
      setIsEmptyPropertiesModalOpen(true);
    }
  }, [isPropertiesEmpty]);

  const theme = useTheme();

  const { mutate: lockFiliationMutation, isPending: lockFiliationPending } = useLockFiliationMutation({
    onSuccess: () => {
      enqueueConfirmSnackbar(
        previousModalResponse === "Oui"
          ? "Vous ne recevrez plus de notifications pour ce sous-dossier."
          : previousModalResponse === "Non"
            ? "Vous recevrez une notification pour chaque nouveau bien identifié."
            : "Aucune action n'a été effectuée.",
        () => {
          setBlockUi(false);
          navigate("/folders");
        },
        "Compris",
        {
          persist: true,
        }
      );
      setPreviousModalResponse(null);
    },
    onError: (error) => {
      console.error(error);
      // Close all potential already as all snackbar are persistent and displayed only one at a time. So we give priority to the error one if user hasn't closed the previous one.
      closeSnackbar();
      // Display the error message from the request if any or the following message if no error message is provided.
      enqueueSnackbar(
        "Une erreur est survenue lors du verrouillage",
        {
          variant: "error",
          persist: true,
          action: (snackbarId: SnackbarKey | undefined) => {
            setBlockUi(false);
            return <SnackbarCloseIcon handleClose={() => closeSnackbar(snackbarId)} />;
          },
        }
      );
    },
  });

  const {
    mutate: setFiliation,
    isPending: setFiliationPending,
    isSuccess: setFiliationSuccess,
  } = useSetFiliationMutation({
    onSuccess: () => {
      setIsConfirmModalOpen(true);
      setBlockUi(true);
      console.log("Filiation set successfully");
      queryClient.invalidateQueries({ queryKey: ["getFiliation", folderId, flowId] });
    },
    onError: (error: any) => {
      console.error(error);
      // Close all potential already as all snackbar are persistent and displayed only one at a time. So we give priority to the error one if user hasn't closed the previous one.
      closeSnackbar();

      if (error?.response?.status === 429) {
        // Refetch to get the latest predictor data
        console.error("Predictor is already running");
        queryClient.invalidateQueries({ queryKey: ["getFiliation", folderId, flowId] });
        setIsPredictorModalOpen(true);
      } else {
        console.error("Error in setFiliation mutation", error);
        // Display the error message from the request if any or the following message if no error message is provided.
        enqueueSnackbar(
          "Une erreur est survenue lors de l'enregistrement de la filiation",
          {
            variant: "error",
            persist: true,
            action: (snackbarId: SnackbarKey | undefined) => (
              <SnackbarCloseIcon handleClose={() => closeSnackbar(snackbarId)} />
            ),
          }
        );
      }
    },
  });

  const dossierData = filiationData?.filiation_data?.dossier_data;

  /**
   * @description Filtrer les communes qui ont au moins un bien associé.
   * Une commune est gardée si elle a au moins une parcelle, un volume ou un lot qui la référence.
   */
  const communesWithProperties = useMemo(() => {
    if (!communes || communes.length === 0) return [];

    return communes.filter((commune: any) => {
      const communeId = commune.id;

      const hasParcelles = parcellesWithNoChildren.some(
        (parcelle: any) => parcelle.parents_ids?.includes(communeId)
      );

      const hasVolumes = volumesWithNoChildren.some(
        (volume: any) => volume.parents?.some((parent: any) => parent.parents_ids?.includes(communeId))
      );

      const hasLots = lots.some(
        (lot: any) => lot.parents?.some((parent: any) => parent.parents_ids?.includes(communeId))
      );

      return hasParcelles || hasVolumes || hasLots;
    });
  }, [communes, parcellesWithNoChildren, volumesWithNoChildren, lots]);

  /**
   * @description Pre-rendering filtering of lots, parcelles, and volume fetched data that adapt thier content depending on the selected commune.
   *
   * @memoization
   * @dependency {obj[]} lots - Lots received from the API in the filiation.
   * @dependency {obj[]} parcellesWithNoChildren - Parcelles received from the API in the filiation.
   * @dependency {obj[]} volumesWithNoChildren - volumes received from the API in the filiation.
   * @dependency {number} selectedCommune - The selected commune to filter the data.
   *
   * @returns {obj} filteredData - The filtered data to be displayed in the filiation list.
   */
  const filteredData = useMemo<FilteredData>(() => {
    return {
      filteredLots: (lots ?? []).filter((lot) => {
        if (selectedCommune >= 0) {
          return (lot?.parents ?? []).some((parent) => parent?.parents_ids?.includes(selectedCommune));
        }
        return true;
      }),
      filteredParcelles: (parcellesWithNoChildren ?? []).filter((parcelle: { parents_ids: number[] }) => {
        if (selectedCommune >= 0) {
          return parcelle?.parents_ids?.includes(selectedCommune);
        }
        return true;
      }),
      filteredVolumes: (volumesWithNoChildren ?? []).filter((volume: { parents: any[] }) => {
        if (selectedCommune >= 0) {
          return (volume?.parents ?? []).some((parent) => parent?.parents_ids?.includes(selectedCommune));
        }
        return true;
      }),
    };
  }, [lots, selectedCommune, parcellesWithNoChildren, volumesWithNoChildren]);


  /**
   * @description Callback function to handle the selection of filiation properties.
   *
   * This function is responsible to update the selected filiation properties when the user
   * manually select a lot, volume, or parcelle.
   *
   * @param {number[]} ids - IDs of selected properties
   * @param {boolean} checked - Whether the properties are being selected or deselected
   *
   * @dependency {number[]} selectedProperties - The selected properties to be updated
   * @dependency {function} setSelectedProperties - The function to update the selected properties
   */
  const handleSelectProperty = useCallback(
    (ids: number[], checked: boolean): void => {
      if (checked) {
        setSelectedProperties((prev) => [...prev, ...ids]);
      } else {
        setSelectedProperties((prev) => prev.filter((id) => !ids.includes(id)));
      }
    },
    [selectedProperties, setSelectedProperties]
  );

  /**
   * @description Callback function to handle the closing of the confirmation modal.
   *
   * @param {React.MouseEvent} event - The event that triggered the closing of the modal
   * @param {string} reason - The reason why the modal is being closed
   *
   * @callback
   * @dependency {function} setIsConfirmModalOpen - setState function to update isConfirmModalOpen state
   */
  const handleCloseModal = useCallback(
    (event: React.MouseEvent, reason: string): void => {
      if (reason === "backdropClick") {
        event.stopPropagation();
        event.preventDefault();
      } else {
        setIsConfirmModalOpen(false);
      }
    },
    [setIsConfirmModalOpen]
  );

  const handleChange = (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  /**
   * @description Side effect that updates the selected properties of each lots when the user manually select a lot.
   *
   * This effect is responsible to update the checkbox in render to representing a user choice and
   * adding it to already chosen lots.
   *
   * @effect
   * @dependency {obj[]} lots - Filiation lots objects
   * @dependency {function} setSelectedProperties - setState function to update the selected properties
   */
  useEffect(() => {
    if (lots && lots?.length && lots.length > 0) {
      const chosenLots = lots
        .map((chosenLot) => {
          if (chosenLot.chosen === true) {
            return chosenLot.id;
          } else {
            return null;
          }
        })
        .filter((chosenLot) => chosenLot !== null);
      setSelectedProperties((prev) => [...prev, ...chosenLots]);
    }
  }, [lots, setSelectedProperties]);

  /**
   * @description Side effect that track changes of the parcelle received in the filiation from the API.
   *
   * This effect is responsible to update the selected parcelles with parcelles from the filiation that can have the property chosen to true.
   *
   * This allow the user to see what parcelles are already chose before making the filiation choice.
   *
   * @dependency {obj[]} parcellesWithNoChildren - Parcelles received from the API in the filiation.
   * @dependency {function} setSelectedProperties - setState function to update the selected properties
   */
  useEffect(() => {
    if (parcellesWithNoChildren && parcellesWithNoChildren?.length && parcellesWithNoChildren.length > 0) {
      const chosenParcellesWithNoChildren = parcellesWithNoChildren
        .map((chosenParcelle: any) => {
          if (chosenParcelle.chosen === true) {
            return chosenParcelle.id;
          } else {
            return null;
          }
        })
        .filter((chosenParcelle: any) => chosenParcelle !== null);
      setSelectedProperties((prev) => [...prev, ...chosenParcellesWithNoChildren]);
    }
  }, [parcellesWithNoChildren, setSelectedProperties]);

  /**
   * @description Side effect that track changes of the volumes received in the filiation from the API.
   *
   * This effect is responsible to update the selected volumes with volumes from the filiation that can have the property chosen to true.
   *
   * This allow the user to see what volumes are already chose before making the filiation choice.
   *
   * @dependency {obj[]} volumesWithNoChildren - volumes received from the API in the filiation.
   * @dependency {function} setSelectedProperties - setState function to update the selected properties
   */
  useEffect(() => {
    if (volumesWithNoChildren && volumesWithNoChildren?.length && volumesWithNoChildren.length > 0) {
      const chosenVolumes = volumesWithNoChildren
        .map((chosenVolume: any) => {
          if (chosenVolume.chosen === true) {
            return chosenVolume.id;
          } else {
            return null;
          }
        })
        .filter((chosenVolume: any) => chosenVolume !== null);
      setSelectedProperties((prev) => [...prev, ...chosenVolumes]);
    }
  }, [volumesWithNoChildren, setSelectedProperties]);

  /**
   * @description Side effect to check predictor status when component loads
   */
  useEffect(() => {
    if (filiationData?.predictor_active?.status === "active") {
      setIsPredictorModalOpen(true);
    }
  }, [filiationData]);

  /**
   * @description Side effect responsible to close all snackbars when the component is unmounted.
   *
   * @effect
   */
  useEffect(() => {
    return () => {
      // Close all snackbars when the component is unmounted
      closeSnackbar();
      console.log("Unmounting Filiation component");
      setIsPredictorModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["getFiliation", folderId, flowId] });
    };
  }, []);

  /**
   * @description Side effect to set Crisp metadata when entering the filiation page
   */
  useEffect(() => {
    const isDev = process.env.REACT_APP_BASE_URL_API_ADDIN === 'https://dev-addin-api.bylaw.fr';
    if (isDev && window.$crisp && dossierData) {
      (window as any).setCrispMetadata?.({
        dossier_id: dossierData.dossier_id,
        sous_dossier_id: dossierData.sous_dossier_id
      });
    }
  }, [dossierData]);

  /**
   * @description Side effect to update Crisp metadata when leaving the filiation page
   */
  useEffect(() => {
    return () => {
      const isDev = process.env.REACT_APP_BASE_URL_API_ADDIN === 'https://dev-addin-api.bylaw.fr';
      if (isDev && window.$crisp && dossierData) {
        (window as any).setCrispMetadata?.({
          dossier_id: `(left) ${dossierData.dossier_id}`,
          sous_dossier_id: `(left) ${dossierData.sous_dossier_id}`
        });
      }
    };
  }, [dossierData]);

  const filiationLoading = setFiliationPending || lockFiliationPending;

  return (
    <>
      

      <Box sx={{ 
        width: "100%", 
        height: "100%",
        minHeight: "100vh",
        display: "flex", 
        flexDirection: "column"
      }}>
        {/* Scrollable content */}
        <Box sx={{ flex: 1, p: 1, pb: 2 }}>
          <Stack spacing={0.5}>

          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Box sx={{ mb: 0.5 }}>
                {/* Title */}
                <Typography fontSize={12} color="#1C1D24" fontWeight={600} sx={{ maxWidth: "75vw" }} noWrap>
                  {client_id} - {userData?.client_name}
                </Typography>
                <Typography fontSize={12} color="#1C1D24" fontWeight={600} sx={{ maxWidth: "75vw" }} noWrap>
                  Dossier: {dossierData?.dossier_name} - {dossierData?.dossier_id}
                </Typography>
                <Typography fontSize={12} color="#1C1D24" fontWeight={600} sx={{ maxWidth: "75vw" }} noWrap>
                  Sous Dossier: {dossierData?.sous_dossier_name} - {dossierData?.sous_dossier_id}
                </Typography>
              </Box>

              {/* Subtitle */}
              <Typography fontSize={12} color="primary.500" fontWeight={600} sx={{ my: 0 }}>
                Sélection des biens objets de la transaction:
              </Typography>
            </Box>


          </Stack>

          {/* Communes Select */}
          <Box sx={{ flexShrink: 0 }}>
            <StyledSelect
              label="Commune"
              disabled={!!blockUi}
              borderColor={theme.palette.grey[900]}
              value={selectedCommune}
              onChange={(e) => setSelectedCommune(Number(e.target.value))}
            >
              <MenuItem value={-1}>Toutes les communes</MenuItem>
              {communesWithProperties?.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.value}
                </MenuItem>
              ))}
            </StyledSelect>
          </Box>

          {/* Filiation tables */}
          <Box>
            <Grid container spacing={1} columns={1} justifyContent="flex-start">
              <Grid item xs={1}>
                <FiliationList
                  blockUi={blockUi || filiationData?.predictor_active?.status === "active"}
                  color="success"
                  title="Parcelles entières :"
                  noDataMessage="Aucune parcelle à afficher"
                  indicator="(seules les parcelles ne contenant aucun volume ni lot sont affichées)"
                  data={filteredData.filteredParcelles}
                  type="cadastre"
                  selectedProperties={selectedProperties}
                  onSelectProperty={handleSelectProperty}
                  loading={isLoading}
                  colNum={0}
                  handleChange={handleChange}
                  expanded={expanded}
                />
              </Grid>
              <Grid item xs={1}>
                <FiliationList
                  blockUi={blockUi || filiationData?.predictor_active?.status === "active"}
                  color="warning"
                  title="Volumes entiers :"
                  noDataMessage="Aucun volume à afficher"
                  indicator="(seuls les volumes ne contenant aucun lot sont affichés)"
                  data={filteredData.filteredVolumes}
                  type="volume"
                  selectedProperties={selectedProperties}
                  onSelectProperty={handleSelectProperty}
                  loading={isLoading}
                  colNum={2}
                  handleChange={handleChange}
                  expanded={expanded}
                />
              </Grid>
              <Grid item xs={1} >
                <FiliationList
                  blockUi={blockUi || filiationData?.predictor_active?.status === "active"}
                  color="primary"
                  title="Lots entiers :"
                  noDataMessage="Aucun lot à afficher"
                  indicator="(vous pouvez filtrer les lots par parcelle et par volume)"
                  data={filteredData.filteredLots}
                  type="lot"
                  selectedProperties={selectedProperties}
                  onSelectProperty={handleSelectProperty}
                  loading={isLoading}
                  colNum={3}
                  handleChange={handleChange}
                  expanded={expanded}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Actions - Sticky at bottom */}
          <Box sx={{ 
            position: 'sticky',
            bottom: 8,
            mt: 2,
            pt: 1.5, 
            pb: 1.5, 
            backgroundColor: 'transparent',
            pointerEvents: 'none', // Allow clicking through the container
            zIndex: 10
          }}>
            <Stack direction="row" gap={3} justifyContent="center" sx={{ pointerEvents: 'auto' }}>
            <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate("/folders");
                  setIsPredictorModalOpen(false);
                }}
                disabled={filiationLoading || !!blockUi}
                sx={{
                  backgroundColor: "#DBE4FE",
                  color: "#3660f4",
                  borderRadius: "10px",
                  "&:hover": {
                    backgroundColor: "rgba(219, 228, 254, 0.8)",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(219, 228, 254, 0.5)",
                    color: "rgba(54, 96, 244, 0.5)",
                  },
                }}
              >
                Annuler
              </Button>
                {filiationData?.predictor_active?.status === "active" ? (
                // If the predictor is active, show a disabled "Valider" button with a tooltip indicating that an analysis is in progress
                <WarningTooltip title="Une analyse est en cours. Veuillez patienter.">
                  <span>
                  <Button 
                    variant="contained" 
                    color="success" 
                    disabled
                    sx={{
                      borderRadius: "10px",
                    }}
                  >
                  Valider
                  </Button>
                  </span>
                </WarningTooltip>
                ) : !selectedProperties.length ? (
                // If no properties are selected, show a disabled "Valider" button with a tooltip indicating that at least one property must be selected
                <WarningTooltip title="Vous devez sélectionner au moins un bien">
                  <span>
                  <Button 
                    variant="contained" 
                    color="success" 
                    disabled
                    sx={{
                      borderRadius: "10px",
                    }}
                  >
                  Valider
                  </Button>
                  </span>
                </WarningTooltip>
                ) : (
                // If the predictor is not active and properties are selected, show an enabled "Valider" button
                <WarningTooltip title="">
                  <span>
                  <Button
                  variant="contained"
                  color="success"
                  disabled={filiationLoading || !!blockUi} // Disable the button if filiation is loading or the UI is blocked
                  onClick={() => {
                  setFiliation({
                    folderId: folderId || "", // Pass the folder ID
                    flowId: flowId || "", // Pass the flow ID
                    properties: getValidatedProperties(selectedProperties), // Pass the validated selected properties
                  });
                  }}
                  sx={{
                    ...(filiationLoading || !!blockUi ? {
                      // Keep default gray styling when disabled
                      borderRadius: "10px",
                    } : {
                      color: "white",
                      backgroundColor: "#3660f4",
                      borderColor: "#3660f4",
                      borderRadius: "10px",
                      "&:hover": {
                        backgroundColor: "rgba(54, 96, 244, 0.8)",
                      },
                    })
                  }}
                  >
                  Valider
                  </Button>
                  </span>
                </WarningTooltip>
                )}
            </Stack>
          </Box>

        </Stack>
        </Box>

      </Box>

      {/* Blocking UI preventing user to udate filiation when saving is in progress */}
      {blockUi ? (
        <Backdrop
          sx={{ color: "rgba(0,0,0,1)", zIndex: (theme) => theme.zIndex.drawer + 1, height: "100%" }}
          open={blockUi}
          onClick={undefined}
        ></Backdrop>
      ) : null}

      {/* Confirmation pop-up to lock the filiation */}
      <Dialog
        keepMounted={false}
        open={isConfirmModalOpen && setFiliationSuccess}
        onClose={handleCloseModal}
        aria-labelledby="confirm-lock-dialog-title"
        disableEscapeKeyDown
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            setIsConfirmModalOpen(false);
            setBlockUi(false);
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Typography
            variant="body1"
            align="center"
            sx={{ my: 2, color: theme.palette.primary.main, fontWeight: "bold" }}
          >
            Avez-vous trouvé l'ensemble des biens objets de votre mutation ?
          </Typography>

          <Typography variant="body2" align="center" sx={{ my: 2, color: theme.palette.secondary.main }}>
            <strong>Oui</strong>: Vous ne recevrez plus de notifications pour cette filiation.
            <br />
            <strong>Non</strong>: Vous recevrez une notification pour chaque nouveau bien identifié.
            <br />
          </Typography>
          <Typography variant="body2" align="center" sx={{ my: 2, color: theme.palette.secondary.main }}>
            <i>Vous pourrez toujours modifier votre choix de filiation dans les paramètres du sous-dossier.</i>
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            pb: 3,
            gap: 2,
          }}
        >
          <Button
            onClick={() => {
              setPreviousModalResponse("Non");
              setIsConfirmModalOpen(false);
              lockFiliationMutation({
                folderId: folderId || "",
                flowId: flowId || "",
                lockValue: false,
              });
            }}
            color="primary"
            variant="contained"
            sx={{ width: 120 }}
          >
            Non
          </Button>

          <Button
            onClick={() => {
              setPreviousModalResponse("Oui");
              setIsConfirmModalOpen(false);
              lockFiliationMutation({
                folderId: folderId || "",
                flowId: flowId || "",
                lockValue: true,
              });
            }}
            color="success"
            variant="outlined"
            sx={{ width: 120 }}
          >
            Oui
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isPredictorModalOpen}
        onClose={filiationData?.predictor_active?.filiation_chosen_by_user_mail ? () => setIsPredictorModalOpen(false) : () => {}}
        aria-labelledby="predictor-dialog-title"
        aria-describedby="predictor-dialog-description"
        disableEscapeKeyDown={!filiationData?.predictor_active?.filiation_chosen_by_user_mail}
        sx={{
          "& .MuiDialog-paper": {
            width: "100%",
            maxWidth: "400px",
            margin: 2,
          },
        }}
      >
        <DialogContent>
          {filiationData?.predictor_active?.filiation_chosen_by_user_mail ? (
            <>
              <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                Le choix des biens a déjà été effectué par l'utilisateur suivant :
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {filiationData.predictor_active.filiation_chosen_by_user_mail}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filiationData?.predictor_active?.timestamp 
                  ? 'le ' + formatParisTime(filiationData.predictor_active.timestamp)
                  : ''}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                align="center"
                sx={{ mt: 2, mb: 2, color: theme.palette.primary.main, fontWeight: "bold" }}
              >
                Un traitement est en cours.
              </Typography>
              <Typography variant="body2" align="center" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                Veuillez patienter pendant l'analyse de votre dossier.
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              navigate("/folders");
              setIsPredictorModalOpen(false);
            }}
          >
            Retour
          </Button>
          {filiationData?.predictor_active?.filiation_chosen_by_user_mail && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setIsPredictorModalOpen(false);
              }}
            >
              Consulter
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal "Aucun bien détecté" */}
      <Dialog
        open={isEmptyPropertiesModalOpen}
        onClose={() => {}} // Empêcher la fermeture par clic extérieur
        aria-labelledby="empty-properties-dialog-title"
        disableEscapeKeyDown
        sx={{
          "& .MuiDialog-paper": {
            width: "100%",
            maxWidth: "450px",
            margin: 2,
          },
        }}
      >
        <DialogContent>
          <Typography
            variant="body1"
            align="center"
            sx={{ mt: 2, mb: 2, color: theme.palette.primary.main, fontWeight: "bold" }}
          >
            La sélection des biens n'est pas encore disponible.
          </Typography>
          <Typography variant="body2" align="center" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            Veuillez patienter pendant l'analyse de votre dossier.
          </Typography>
          <Typography variant="body2" align="center" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            Nous vous invitons également à vérifier que votre dossier contient : un état hypothécaire et un titre de propriété correspondant au(x) bien(s) concerné(s).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setIsEmptyPropertiesModalOpen(false);
              window.close();
            }}
            sx={{
              borderRadius: "10px",
            }}
          >
            Compris
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop pour griser la page quand le modal est ouvert */}
      {isEmptyPropertiesModalOpen && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer }}
          open={isEmptyPropertiesModalOpen}
        />
      )}

    </>
  );
}
